import React, { useRef, useState } from 'react';
import {
  motion,
  useAnimationFrame,
  useMotionValue,
  useScroll,
  useSpring,
  useTransform,
  useVelocity,
} from 'framer-motion';

interface VelocityMarqueeProps {
  children: React.ReactNode;
  /** Ambient drift in pixels per second. Negative runs right-to-left. */
  speed?: number;
  className?: string;
  /** Ease down to this fraction of the base speed while hovered */
  hoverSlow?: number;
}

const wrap = (min: number, max: number, v: number) => {
  const range = max - min;
  return ((((v - min) % range) + range) % range) + min;
};

/**
 * Infinite marquee whose speed and direction react to scroll velocity: scroll
 * fast and the text whips along with you, scroll up and it reverses. The
 * ambient drift is set in px/s so every line reads at the same pace whatever
 * it happens to say.
 *
 * Hovering eases it down to a crawl (never a hard stop) so a passing line can
 * actually be read, then it winds back up when the pointer leaves.
 *
 * It can also be grabbed and thrown. Dragging scrubs the line directly, and
 * letting go hands the release velocity to a decaying flick that settles back
 * into the ambient drift, keeping whichever direction it was thrown. Pointer
 * events cover mouse and touch alike; touch-action: pan-y means a horizontal
 * swipe grabs the line while a vertical one still scrolls the page.
 */
const VelocityMarquee: React.FC<VelocityMarqueeProps> = ({
  children,
  speed = 70,
  className = '',
  hoverSlow = 0.12,
}) => {
  const baseX = useMotionValue(0);
  const { scrollY } = useScroll();
  const scrollVelocity = useVelocity(scrollY);
  const smoothVelocity = useSpring(scrollVelocity, { damping: 50, stiffness: 400 });
  const velocityFactor = useTransform(smoothVelocity, [0, 1000], [0, 4], { clamp: false });
  const directionFactor = useRef(1);

  const [hovered, setHovered] = useState(false);
  // eased rather than snapped, so the change of pace reads as deliberate
  const speedRef = useRef(1);

  const trackRef = useRef<HTMLDivElement>(null);
  const dragging = useRef(false);
  const lastPointerX = useRef(0);
  const lastMoveAt = useRef(0);
  // percent of the track per second, decaying, applied after release
  const flick = useRef(0);

  /** px along the screen -> percent of the track, which is what baseX speaks */
  const toPercent = (px: number) => {
    const w = trackRef.current?.scrollWidth ?? 0;
    return w ? (px / w) * 100 : 0;
  };

  const onPointerDown = (e: React.PointerEvent) => {
    dragging.current = true;
    flick.current = 0;
    lastPointerX.current = e.clientX;
    lastMoveAt.current = performance.now();
    // Capture keeps the drag alive if the finger leaves the line, but WebKit
    // throws for a pointer id it does not recognise, and an exception here
    // would abandon the drag before it starts.
    try {
      (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    } catch {
      /* drag still works, it just ends if the pointer leaves */
    }
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (!dragging.current) return;
    const dx = e.clientX - lastPointerX.current;
    const now = performance.now();
    const dt = Math.max(1, now - lastMoveAt.current);
    lastPointerX.current = e.clientX;
    lastMoveAt.current = now;
    baseX.set(baseX.get() + toPercent(dx));
    // px/ms -> percent/s, smoothed so one jittery sample cannot dominate
    flick.current = flick.current * 0.6 + toPercent(dx / dt) * 1000 * 0.4;
  };

  const endDrag = (e: React.PointerEvent) => {
    if (!dragging.current) return;
    dragging.current = false;
    try {
      (e.currentTarget as HTMLElement).releasePointerCapture?.(e.pointerId);
    } catch {
      /* nothing to release */
    }
    // carry the throw, and let it decide which way the line drifts afterwards
    if (Math.abs(flick.current) > 0.5) directionFactor.current = Math.sign(flick.current);
  };

  // 4 identical segments; wrap over one segment width (-25%..0%)
  const x = useTransform(baseX, (v) => `${wrap(-25, 0, v)}%`);

  useAnimationFrame((_, delta) => {
    // While the line is held it belongs to the pointer, not the clock.
    if (dragging.current) return;

    const target = hovered ? hoverSlow : 1;
    speedRef.current += (target - speedRef.current) * Math.min(1, delta / 220);

    // baseX counts percent of the track, so a speed expressed as a percentage
    // makes a long line run faster than a short one in the unit that actually
    // matters, pixels on screen. The About line carries about six times the
    // text of the Contact line and was drifting at ~575 px/s against its ~98,
    // from settings that looked all but identical in the markup. Convert the
    // px/s the caller asked for using the track's real width instead.
    const trackW = trackRef.current?.scrollWidth ?? 0;
    if (!trackW) return;

    let moveBy = directionFactor.current * ((speed / trackW) * 100) * (delta / 1000);
    const vf = velocityFactor.get();
    if (vf < 0) directionFactor.current = -1;
    else if (vf > 0) directionFactor.current = 1;
    moveBy += directionFactor.current * moveBy * Math.abs(vf);

    if (flick.current !== 0) {
      moveBy += flick.current * (delta / 1000);
      // ~85% of the throw survives each 16ms, so it coasts rather than stops
      flick.current *= Math.pow(0.9, delta / 16);
      if (Math.abs(flick.current) < 0.05) flick.current = 0;
    }

    baseX.set(baseX.get() + moveBy * speedRef.current);
  });

  return (
    <div
      className={`overflow-hidden whitespace-nowrap select-none cursor-grab active:cursor-grabbing ${className}`}
      style={{ touchAction: 'pan-y' }}
      data-cursor="DRAG"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
    >
      <motion.div
        ref={trackRef}
        className="inline-flex whitespace-nowrap will-change-transform"
        style={{ x }}
      >
        {[0, 1, 2, 3].map((i) => (
          <span key={i} className="inline-flex items-center shrink-0">
            {children}
          </span>
        ))}
      </motion.div>
    </div>
  );
};

export default VelocityMarquee;
